// DefectConversation — shared per-item conversation thread + reply composer.
//
// v0.127.0 — EXTRACTED from PlanScreen's DefectBottomSheet (v0.122.0 thread
// + composer, v0.123.0 comment-photo lightbox) so BOTH defect sheets render
// the same conversation: PlanScreen's editor modal (pinned items) AND
// RegisterScreen's read-only sheet (unpinned items — e.g. items pushed in
// from a builder, which have no drawing pin and therefore never reach the
// plan editor). Brett's phone report: tapping a Question chip on an
// unpinned item opened the read-only sheet, which had NO conversation
// section — the question was invisible and unanswerable.
//
// Behaviour is unchanged from the in-sheet original:
//   * fail-soft fetch of /api/defect-comments?defectId=N — a comments
//     outage hides the section, never breaks the host sheet;
//   * questions pushed back by the report's recipient render framed +
//     brand-accented ("someone is waiting on an answer"); every other
//     role renders as a plain entry;
//   * comment photo thumbnails open the shared PhotoLightbox full-size;
//   * the reply composer POSTs then refreshes the thread in place.
//
// Props:
//   defectId             — the item id. null/undefined (a brand-new pin)
//                          renders nothing, same as the original gate.
//   scrollIntoViewOnLoad — when true, the section scrolls itself into view
//                          once the thread arrives (register question-chip
//                          open). PlanScreen omits it → no scroll, exactly
//                          as before the extraction.
//   layout               — v0.272.0. 'stacked' (default): a section inside
//                          the host's scroll, as before. 'panel': fills a
//                          column of its own (the item editor's right-hand
//                          side on a wide screen — Brett: "lets split this
//                          screen so coversation is on the right"); the
//                          thread scrolls, the composer stays at the bottom.
//   composerRef          — v0.272.0. Lets the host read and post what is
//                          typed but not sent: { draft(), post() }. Brett
//                          typed a comment, pressed the item's Save, and the
//                          words were lost. post() answers true once posted.
//                          v0.279.0 — and postPhoto({ photoUrl,
//                          photoThumbnailUrl, photoDisplayUrl }) posts a
//                          marked-up photo as the office's message, answering
//                          { ok, held } or { ok: false, error }.
//   onAddedToItem(photo) — v0.272.0. A client's reply photo the office has
//                          just added to the item's photos.
//   photoArea            — v0.274.0. Where a message's photo opens (the
//                          viewer's area): the item side of a split card, so
//                          the conversation stays open beside it.
//
// v0.277.0 — PAUSE SENDING (office only). Brett: "a pause button specifically
// for [our] staff, which means it doesnt send until we un pause it." The pause
// covers the item's whole job: while it is on, every office message is saved
// and marked "Held — not sent yet", and the client's page, the recipient and
// Send update see none of them; Resume sends them all at once.
//
// v0.283.0 — TWO CHATS. Brett: "Different chat for subbies and clients." Each
// item has the client's chat and the subcontractors' chat (api/shared/chats);
// the office card shows them on two tabs, each with its own messages, Seen
// by and Pause. The tabs appear once the server has both chats (or a message
// is already in the trades' one); before that there is one conversation, the
// client's, exactly as it was. The card opens on the chat of the newest
// message from outside the office.
//
// v0.290.0 — ONE LOOK. The heading, tabs, message box, messages and reply box
// are ConversationParts, which the client's report page draws too (Brett: "Why
// isnt it like the app?"). What only the office has — Pause, Remove, "Seen
// by", "Held", Add to item photos — is still made here and handed in.
//
// v0.321.0 — RESPONSE REQUIRED (office only). Brett: "I need a button and
// indicator that screams out that there is an outstanding response required"
// / "Could we have like a 3 dot button to the comment line and we could flag
// as response required". Any office person: ⋯ on a message → Flag: response
// required (or Clear flag). A flagged message wears a red edge and a
// "Response required" tag, its chat tab a red dot. A reply does not clear it.
// v0.322.0 — Brett, on the card's red banner: "thats too much, remove it. just
// the redbox is fine". The banner (Show / Mark responded) is gone; the red box
// on the message is the mark, cleared from its ⋯ (Clear flag). A client or
// subbie sees the flags in their own chat and can flag back (api/public-flag):
// the tag's hover says who flagged it.

import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { apiGet, apiSend, fmtDate } from '../api';
import { markApiMutated } from '../utils/apiFreshness.js';
import { theme, btnSecondary } from '../theme';
import PhotoLightbox from './PhotoLightbox';
import { isClientContactSession } from '../utils/contact-session';
import { sessionHasEnded } from '../utils/sessionEnd';
import { markRepliesSeen } from './NewReplies';
import { announceFlagsChanged } from './ResponseFlags';
import { Flag } from 'lucide-react';
import { watchLive, presenceBeacon } from '../utils/live';
import {
  chatOf, isOutsider, messageLabel, MessageAvatar, ConversationLabel,
  ChatTabs, ThreadBox, MessageRow, MessageText, MessagePhoto, MessagePhotos, Composer,
  PhotoAttachments, AddPhotosButton, FlagTag, MessageMenuButton,
} from './ConversationParts';
import { photoRuns, runSeenBy } from '../utils/photoRuns';
import { MAX_CHAT_PHOTOS, photosForChat, photosForChatError } from '../utils/chatPhotos';
import { makeImageVariants } from '../utils/imageVariants';

// v0.280.0 - when a person last had an office message on screen: day and time.
function seenWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

// v0.276.0 — the company's logo and name for the office's own messages,
// read once per page (the badge falls back to initials while it loads, and
// for good if there is no logo). A stored 'blob:' reference is not a picture
// the browser can show, so it counts as no logo.
let tenantBrand = null;
function loadTenantBrand() {
  if (!tenantBrand) {
    tenantBrand = apiGet('/api/tenant')
      .then((t) => {
        const logo = (t && t.brandLogoUrl) || null;
        return { logoUrl: logo && !/^blob:/i.test(logo) ? logo : null, name: (t && t.brandName) || '' };
      })
      .catch(() => { tenantBrand = null; return { logoUrl: null, name: '' }; });
  }
  return tenantBrand;
}

// v0.277.0 — the job's pause, straight from the network: a saved copy saying
// "not paused" would lie about where a message goes. { available, paused,
// pausedAt, pausedBy, held }; available:false = not set up here (no button).
// v0.283.0 — one chat's pause (each is paused on its own); chats:true = the
// server has both chats.
async function readPause(defectId, chat = 'client') {
  const q = '/api/conversation-pause?defectId=' + encodeURIComponent(defectId) + (chat === 'trade' ? '&chat=trade' : '');
  const r = await fetch(q, { cache: 'no-store', credentials: 'same-origin' });
  if (!r.ok) { const e = new Error('pause ' + r.status); e.status = r.status; throw e; }
  return r.json();
}

export default function DefectConversation({ defectId, scrollIntoViewOnLoad = false, layout = 'stacked', composerRef = null, onAddedToItem = null, photoArea = null }) {
  const panel = layout === 'panel';
  // v0.122.0 — per-item conversation. The recipient of a sent report can
  // push a question back about this item (/api/report-feedback stores it as
  // a 'recipient' comment); the thread + a reply composer render at the
  // bottom of the sheet. null = not loaded / fetch failed → section hidden
  // (fail-soft: a comments outage must never break the defect editor).
  const [comments, setComments]         = useState(null);
  const [replyText, setReplyText]       = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyErr, setReplyErr]         = useState('');
  // v0.123.0 — a comment's attached photo (recipient question photos,
  // rectify evidence) opened full-size. null = closed.
  const [commentPhoto, setCommentPhoto] = useState(null);
  // v0.272.0 — the panel says it could not load rather than standing empty.
  const [loadFailed, setLoadFailed]     = useState(false);
  // v0.272.0 — "Add to item photos": the comment being added, its error, and
  // the ones added while this is open (the list says so on its next read).
  const [adding, setAdding]             = useState(null);
  const [addErr, setAddErr]             = useState(null);   // { id, msg }
  const [addedIds, setAddedIds]         = useState(() => new Set());
  const office = !isClientContactSession();
  // v0.289.0 — Brett: "contract administrator and above should moderate",
  // "remove only". Whether this person may (the server says; anything but a
  // yes is no buttons), the message whose choices are open, the choice being
  // confirmed ('remove' | 'remove-person'), and a refusal under its message.
  const [canMod, setCanMod]             = useState(false);
  const [modOpen, setModOpen]           = useState(null);
  const [modAsk, setModAsk]             = useState(null);
  const [modBusy, setModBusy]           = useState(false);
  const [modErr, setModErr]             = useState(null);   // { id, msg }
  // v0.321.0 — a flag or clear on its way (a refusal shows under its message).
  const [flagBusy, setFlagBusy]         = useState(false);
  // v0.276.0 — live: 'live' | 'retrying' | 'paused' | 'ended' | 'off' (null
  // until the watch starts). The company's brand, for the office's messages.
  const [liveState, setLiveState]       = useState(null);
  // v0.336.0 - Try again on "Live off" starts the watch afresh.
  const [liveTry, setLiveTry]           = useState(0);
  const [brand, setBrand]               = useState({ logoUrl: null, name: '' });
  // v0.277.0 — the pause: null until read (a failed read shows no button; a
  // message the server held still says so on its own line).
  const [pause, setPause]               = useState(null);
  const [pauseTry, setPauseTry]         = useState(0);
  const [pauseBusy, setPauseBusy]       = useState(false);
  const [pauseErr, setPauseErr]         = useState('');
  // v0.283.0 — the chat on screen ('client' | 'trade'), whether the server has
  // both, and whether the card has chosen its opening chat yet.
  const [chat, setChat]                 = useState('client');
  const [chatsOn, setChatsOn]           = useState(false);
  const chatPicked = useRef(false);
  const chatRef = useRef(chat);
  chatRef.current = chat;
  // What the presence beacon says this card shows (v0.313.0: on a subbie's
  // tab, which subbie - so only that subbie sees the office there).
  const beatFieldsRef = useRef({ chat: 'client' });
  // Each tab keeps its own unsent words: what was typed to the client can
  // never go to the subcontractors by a tab press.
  // v0.313.0 — keyed by tab: 'client', or 't:<party key>' for a subbie.
  const draftsRef = useRef({});
  // v0.319.0 — photos waiting to go, per tab like the words. Brett
  // (2026-11-04): "build it so i can drag photos into chat." Dragged onto the
  // box or picked with Add photos, up to 10 a message; each goes as its own
  // message (the words with the first), and like the client's photos they
  // stay out of the item's photos until "Add to item photos".
  const [picsBy, setPicsBy]             = useState({});
  const picsRef = useRef({});
  picsRef.current = picsBy;
  const [preparing, setPreparing]       = useState(0);
  const showTabs = office && (chatsOn || (Array.isArray(comments) && comments.some((c) => c.chat === 'trade')));
  // v0.313.0 — Brett: "I think the subby chat should be seperate chats.
  // Otherwise i cant send it to say 2 electricians to quote." One tab per
  // subcontractor this item has been sent to (their links folded by address,
  // from the server: [{ key, shareId, shareIds, name, email, live }]); each tab
  // is a conversation only that subbie reads. A trades' message from before
  // the split (no subbie on it) was read by every subbie, so it shows on every
  // subbie's tab. `chat` is the tab: 'client' or 't:<key>' ('trade' = the
  // placeholder tab while no subbie has been sent the item).
  const [parties, setParties]           = useState(null);
  const party = chat !== 'client' && Array.isArray(parties) ? (parties.find((p) => 't:' + p.key === chat) || null) : null;
  const chatKind = chat === 'client' ? 'client' : 'trade';
  const inTab = (c, tab) => {
    if (tab === 'client') return chatOf(c) === 'client';
    if (chatOf(c) !== 'trade') return false;
    return c.tradeParty == null || 't:' + c.tradeParty === tab;
  };
  const shown = Array.isArray(comments) && showTabs ? comments.filter((c) => inTab(c, chat)) : comments;
  const who = chat === 'client' ? 'the client' : party ? party.name : 'the subcontractors';
  const chatTabs = [['client', 'Client'], ...(Array.isArray(parties) && parties.length
    ? parties.map((p) => ['t:' + p.key, p.name, Array.isArray(p.people) && p.people.length && (p.people.length > 1 || p.people[0] !== p.name)
      ? `${p.name} · ${p.people.join(', ')}`   // v0.329.0 — a company's tab names its people
      : p.email && p.email !== p.name ? `${p.name} · ${p.email}` : p.name])
    : [['trade', 'Subcontractors']])];
  // A subbie tab with no subbie behind it (none sent yet, or still reading
  // who) has nobody to write to.
  const canWrite = !showTabs || chat === 'client' || !!party;
  const pics = picsBy[chat] || [];
  // The pause holds a whole chat: every subbie's, not one.
  const pauseWho = chatKind === 'trade' ? 'the subcontractors' : 'the client';
  const tabPeople = (list) => list.filter((p) => {
    if (chat === 'client') return !p.chat || p.chat === 'client';
    if (p.chat !== 'trade') return false;
    if (p.who !== 'link') return true;   // the office on the trades' chat (which subbie it has open is not told)
    return !!party && Array.isArray(party.shareIds) && party.shareIds.includes(p.linkId);
  });
  const defectRef = useRef(defectId);
  defectRef.current = defectId;
  const showTabsRef = useRef(showTabs);
  showTabsRef.current = showTabs;
  // v0.278.0 — the client sees "<name> is editing this item" while this card
  // is open. Never while sending is paused (a bulk edit is not a conversation),
  // and not until the pause is known: an unknown pause shows nothing.
  const beaconRef = useRef(null);
  // v0.284.0 — Brett: "I thought we could display the favocon at the top of
  // the box if they are on?" Who else is on this item now (office people and
  // the client or subcontractors on their report links), from the live poll:
  // [{ name, typing, who, chat }]. The beacon says which chat this card shows.
  const [presence, setPresence] = useState([]);
  const presenceOk = !!pause && !pause.failed && !pause.paused;
  const presenceOkRef = useRef(presenceOk);
  presenceOkRef.current = presenceOk;

  const rootRef   = useRef(null);
  const threadRef = useRef(null);
  const didScroll = useRef(false);
  // v0.276.0 — the thread follows new messages only while the reader is at the
  // bottom of it: someone scrolled up to read an older message is not pulled
  // down by the next one arriving.
  const stickRef  = useRef(true);

  // 2026-09-15 — Brett: "if im in a plan ... the comments is showing up
  // blank". Every /api read is answered from the phone's saved copy first
  // (the service worker), and this section never re-read when the network's
  // copy arrived, so an old or empty thread stayed on screen. Now: nothing of
  // the previous item is shown, the saved copy is shown at once (reception on
  // site), and the network's copy replaces it when it lands.
  useEffect(() => {
    setComments(null);
    setLoadFailed(false);
    setAddedIds(new Set());
    setAddErr(null);
    setModOpen(null);
    setModAsk(null);
    setModErr(null);
    setChat('client');
    setChatsOn(false);
    chatPicked.current = false;
    draftsRef.current = {};
    setPicsBy({});
    setParties(null);
    didScroll.current = false;
    stickRef.current = true;
    if (!defectId) return;                // new pin — no thread yet
    let cancelled = false;
    let freshLanded = false;
    let savedShown = false;
    const path = '/api/defect-comments?defectId=' + defectId;
    apiGet(path)
      .then((list) => { if (!cancelled && !freshLanded && Array.isArray(list)) { savedShown = true; setComments(list); } })
      .catch(() => { /* the fresh read below decides */ });
    (async () => {
      try {
        const list = await apiGet(path, { fresh: true });
        if (!cancelled && Array.isArray(list)) {
          freshLanded = true; setComments(list);
          // v0.269.0 — the network's copy is what this person has now read:
          // its replies stop being new for them (only them). The saved copy
          // above may be stale, so it never marks anything.
          markRepliesSeen(defectId, list);
        }
      } catch (e) {
        // Fail-soft — keep a saved copy if one showed; with none the section
        // stays hidden rather than breaking the sheet.
        console.warn('[DropPin] could not load defect comments:', e?.message || e);
        if (!cancelled && !savedShown) setLoadFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [defectId]);

  // v0.127.0 — question-chip opens land ON the conversation: once the
  // thread has loaded, scroll the section into view inside the host
  // sheet's scroll container. Runs at most once per mount.
  useEffect(() => {
    if (!scrollIntoViewOnLoad || didScroll.current) return;
    if (!Array.isArray(comments)) return;
    didScroll.current = true;
    requestAnimationFrame(() => {
      try { rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
    });
  }, [comments, scrollIntoViewOnLoad]);

  // v0.272.0 — the panel reads like a chat: newest at the bottom, in view.
  // v0.276.0 — both layouts, and only while the reader is at the bottom (or
  // has just sent): a live message must not yank someone reading higher up.
  useEffect(() => {
    if (!Array.isArray(comments) || !stickRef.current) return;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments, panel, chat]);

  // v0.283.0 — the card opens on the chat someone outside the office last
  // wrote in (the one waiting on an answer); the client's when nobody has.
  useEffect(() => {
    if (!office || !Array.isArray(comments) || chatPicked.current) return;
    chatPicked.current = true;
    const last = comments.filter((c) => c.authorRole === 'recipient' || c.authorRole === 'guest').pop();
    // v0.313.0 — that subbie's own tab; a reply from before the split names
    // no subbie, so the first subbie's (the placeholder settles it below).
    if (last && last.chat === 'trade' && !replyText.trim() && !(picsRef.current[chat] || []).length) setChat(last.tradeParty ? 't:' + last.tradeParty : 'trade');
  }, [comments]);

  // v0.313.0 — who this item has been sent to, read when the tabs show and
  // again whenever the thread is read fresh (a new link, a first reply).
  const loadParties = (id = defectId) => {
    // One chat on this server: no subbie tabs, nobody to ask about.
    if (!id || !office || !showTabsRef.current) return;
    apiGet('/api/defect-comments?defectId=' + id + '&view=parties', { fresh: true })
      .then((list) => { if (defectRef.current === id && Array.isArray(list)) setParties(list); })
      .catch(() => { /* the tabs keep what they had; the next read tries again */ });
  };
  useEffect(() => { if (showTabs && parties === null) loadParties(); }, [showTabs, defectId]);
  // A subbie tab the list does not hold (the placeholder, or a subbie read
  // before the list came) moves to the first subbie, or back to the
  // placeholder when there is none. Never with words in the box.
  useEffect(() => {
    if (!Array.isArray(parties) || chat === 'client' || party) return;
    if (replyText.trim() || (picsRef.current[chat] || []).length) return;
    const next = parties.length ? 't:' + parties[0].key : 'trade';
    if (next !== chat) setChat(next);
  }, [parties, chat]);
  const onThreadScroll = (e) => {
    const el = e.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // v0.276.0 — LIVE. Brett, for a Teams meeting: "if we both have it open, a
  // client could attach a photo and send it to me while im waiting, or text
  // instructions while we are talking." The office's card watches this item
  // through the blob marker (src/utils/live.js — never the database) and
  // re-reads the thread only when the marker says THIS item changed. A
  // client contact signed in to DropPin does not watch (their page is the
  // report link, which watches for itself).
  useEffect(() => {
    setLiveState(null);
    setPresence([]);
    if (!defectId || !office) return;
    let cancelled = false;
    let watcher = null;
    let beacon = null;
    const path = '/api/defect-comments?defectId=' + defectId;
    // { live, presence } — the watch's pass and (v0.278.0) the item's presence pass.
    const mint = async () => {
      const r = await fetch('/api/live-token?defectId=' + encodeURIComponent(defectId), { cache: 'no-store', credentials: 'same-origin' });
      if (!r.ok) {
        // v0.336.0 - a refused pass is said ("Live off - Try again"), never a
        // silent nothing; a 401 is checked against the sign-in, and the shell
        // signs back in if it has ended.
        if (r.status === 401) sessionHasEnded('/api/live-token').catch(() => {});
        const e = new Error('live-token ' + r.status); e.status = r.status; throw e;
      }
      return (await r.json()) || null;
    };
    (async () => {
      let passes = null;
      let failed = false;
      try { passes = await mint(); } catch (_) { passes = null; failed = true; }
      if (cancelled) return;
      if (failed) { setLiveState('failed'); return; }
      if (passes && passes.presence) {
        beacon = presenceBeacon({ pass: passes.presence, enabled: presenceOkRef.current, fields: beatFieldsRef.current });
        beaconRef.current = beacon;
      }
      watcher = watchLive({
        pass: (passes && passes.live) || null,
        getPass: () => mint().then((j) => (j && j.live) || null).catch(() => null),
        since: Date.now(),
        onState: (s) => { if (!cancelled) setLiveState(s); },
        onPresence: (all) => { if (!cancelled) setPresence((all && all[defectId]) || []); },
        onChange: async (ids) => {
          if (!ids.includes(Number(defectId))) return;
          try {
            const list = await apiGet(path, { fresh: true });
            if (!cancelled && Array.isArray(list)) {
              setComments(list);
              markRepliesSeen(defectId, list);
              loadParties(defectId);
            }
          } catch (_) { /* the next change tries again */ }
        },
      });
    })();
    return () => {
      cancelled = true;
      if (watcher) watcher.stop();
      if (beacon) beacon.stop();
      if (beaconRef.current === beacon) beaconRef.current = null;
      setPresence([]);
    };
  }, [defectId, office, liveTry]);
  useEffect(() => {
    if (beaconRef.current) beaconRef.current.setEnabled(presenceOk);
  }, [presenceOk]);
  useEffect(() => {
    beatFieldsRef.current = { chat: chatKind, ...(party ? { party: party.shareId } : {}) };
    if (beaconRef.current) beaconRef.current.setFields(beatFieldsRef.current);
  }, [chatKind, party && party.shareId]);

  // v0.276.0 — Brett: "put in the favicons to the conversation box". The
  // office's messages carry the company's logo; a client contact's view
  // shows initials only (it has no business reading the tenant record).
  useEffect(() => {
    if (!office) return;
    let cancelled = false;
    loadTenantBrand().then((b) => { if (!cancelled) setBrand(b); });
    return () => { cancelled = true; };
  }, [office]);

  // v0.289.0 — asked once; a client contact never asks.
  useEffect(() => {
    if (!office) return;
    let cancelled = false;
    fetch('/api/comment-moderation', { cache: 'no-store', credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setCanMod(!!(j && j.canModerate === true)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [office]);

  // v0.283.0 — the pause of the chat on screen; its answer also says whether
  // the server has both chats (the tabs).
  useEffect(() => {
    setPause(null);
    setPauseErr('');
    if (!defectId || !office) return;
    let cancelled = false;
    readPause(defectId, chatKind).then((p) => {
      if (cancelled) return;
      setPause(p && typeof p === 'object' ? p : { failed: true });
      if (p && p.chats) setChatsOn(true);
    }).catch((e) => {
      // v0.336.0 - a failed read says so ("Pause unavailable - Try again"),
      // never no control at all.
      if (e && e.status === 401) sessionHasEnded('/api/conversation-pause').catch(() => {});
      if (!cancelled) setPause({ failed: true });
    });
    return () => { cancelled = true; };
  }, [defectId, office, chatKind, pauseTry]);
  // v0.313.0 — the pause is per chat (client / trades), not per subbie.
  const kindOf = (tab) => (tab === 'client' ? 'client' : 'trade');
  const refreshPause = () => {
    const id = defectId;
    const which = kindOf(chatRef.current);
    if (!id || !office) return;
    readPause(id, which).then((p) => { if (defectRef.current === id && kindOf(chatRef.current) === which) setPause(p); })
      .catch(() => { if (defectRef.current === id && kindOf(chatRef.current) === which) setPause({ failed: true }); });
  };

  // 2026-08-01 — the send WORKED but said nothing (Brett, on his phone:
  // "no indication that the response message actually sent"). Every outcome
  // now announces itself: sent (flash + the message visible in the thread),
  // queued offline (the outbox marker), or failed (the error line).
  const [sentFlash, setSentFlash] = useState('');
  // v0.267.0 — the office's comment is SAVED, not emailed: Send update emails
  // everything typed since the last one together (Brett: "I dont want them to
  // get 5 emails"). The flash says so, and the bar that counts unsent
  // comments is told to look again.
  // v0.276.0 — and it shows on their open report within seconds (live).
  const savedFlash = isClientContactSession() ? 'Reply sent ✓' : 'Saved ✓ — on their report now (within seconds if they have it open), and emailed in about a minute unless they read it first.';
  // v0.277.0 — the server says whether it held the message (paused job).
  // v0.283.0 — and names the chat it is held from.
  const heldFlash = `Held ✓ — saved here, not sent to ${who} until you resume sending or press Send update.`;
  const flashFor = (list) => {
    const mine = Array.isArray(list) ? list.filter((c) => c.authorRole === 'author').pop() : null;
    return mine && mine.heldAt ? heldFlash : savedFlash;
  };
  // v0.283.0 — a tab press: the words in the box stay with the chat they were
  // typed in, and the other chat's come back.
  const switchChat = (next) => {
    if (next === chat) return;
    draftsRef.current[chat] = replyText;
    setReplyText(draftsRef.current[next] || '');
    draftsRef.current[next] = '';
    stickRef.current = true;
    setSentFlash('');
    setReplyErr('');
    setPauseErr('');
    setChat(next);
  };
  // v0.323.0 - { now } (Resume sending): the strip emails what was released
  // at once rather than after the minute's hold.
  const announcePosted = ({ now = false } = {}) => {
    if (beaconRef.current) beaconRef.current.typing(false);   // the message is there: not "typing" any more
    try { window.dispatchEvent(new CustomEvent('droppin:comment-posted', { detail: { defectId, ...(now ? { now: true } : {}) } })); } catch (_) {}
  };
  // Answers true when the words are posted (or held on this phone to go up
  // with the sync), false when they are not — the item's Save relies on it.
  // v0.283.0 — the words go to the chat they were typed in: the box on screen
  // (no argument), or another tab's kept words ({ body, chat }).
  // v0.313.0 — a subbie's tab posts to that subbie's link.
  const postTo = (tab) => {
    if (!showTabs) return {};
    if (tab === 'client') return { chat: 'client' };
    const p = Array.isArray(parties) ? parties.find((x) => 't:' + x.key === tab) : null;
    return { chat: 'trade', tradeShareId: p ? p.shareId : null };
  };
  const sendReply = async (kept = null) => {
    const which = kept ? kept.chat : chat;
    const body = ((kept ? kept.body : replyText) || '').trim();
    const clear = () => { if (kept) draftsRef.current[which] = ''; else setReplyText(''); };
    const photos = picsRef.current[which] || [];
    if (photos.length) return sendPhotos(which, body, photos, clear);
    if (!body) return true;
    if (replySending) return false;
    setReplySending(true);
    setReplyErr('');
    setSentFlash('');
    try {
      const to = postTo(which);
      if (showTabs && to.chat === 'trade' && !to.tradeShareId) {
        setReplyErr('No subcontractor has been sent this item yet. Nothing was sent.');
        return false;
      }
      const list = await apiSend('POST', '/api/defect-comments', { defectId, body, ...to });
      if (list && list.queued) {
        // Offline outbox took it — honest about when it will actually go.
        // v0.171.0 — in site mode it was HELD on purpose, not lost to reception.
        clear();
        setSentFlash(list.held
          ? 'Saved on this phone — your reply goes up with the next sync.'
          : 'No connection — your reply is saved and will send automatically when you’re back online.');
        return true;
      } else if (Array.isArray(list)) {
        stickRef.current = true;          // your own message: show it
        setComments(list);
        clear();
        setSentFlash(flashFor(list));
        setTimeout(() => setSentFlash(''), 4000);
        announcePosted();
        refreshPause();
        loadParties();
      } else {
        // Unexpected shape — re-fetch so the thread still shows the truth.
        let fresh = null;
        try { fresh = await apiGet('/api/defect-comments?defectId=' + defectId); if (Array.isArray(fresh)) setComments(fresh); } catch { /* keep what we have */ }
        clear();
        setSentFlash(flashFor(fresh));
        setTimeout(() => setSentFlash(''), 4000);
        announcePosted();
        refreshPause();
      }
      return true;
    } catch (e) {
      setReplyErr(e?.message || 'Could not send the reply.');
      return false;
    } finally {
      setReplySending(false);
    }
  };

  // v0.319.0 — the photos waiting in a tab, one message each, the words with
  // the first. Straight to the network like a marked-up photo: ten copies of
  // three sizes do not belong in the offline queue, and the press should hear
  // a refusal now. Each one sent leaves the box at once, so a failure part-way
  // never sends one twice; what is left stays for another try.
  const dropPic = (tab, key) => setPicsBy((prev) => ({ ...prev, [tab]: (prev[tab] || []).filter((p) => p.key !== key) }));
  const sendPhotos = async (which, body, photos, clear) => {
    if (replySending) return false;
    const id = defectId;
    const to = postTo(which);
    setReplyErr('');
    setSentFlash('');
    if (showTabs && to.chat === 'trade' && !to.tradeShareId) {
      setReplyErr('No subcontractor has been sent this item yet. Nothing was sent.');
      return false;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setReplyErr('Photos need a connection — nothing was sent. They are still in the box.');
      return false;
    }
    setReplySending(true);
    let sent = 0;
    let list = null;
    try {
      for (let i = 0; i < photos.length; i++) {
        const pic = photos[i];
        const words = i === 0 ? body : '';
        // eslint-disable-next-line no-await-in-loop
        const v = await makeImageVariants(pic.dataUrl);
        let r;
        try {
          // eslint-disable-next-line no-await-in-loop
          r = await fetch('/api/defect-comments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              defectId: id,
              ...(words ? { body: words } : {}),
              photoUrl: pic.dataUrl,
              photoThumbnailUrl: v.thumbnailUrl || null,
              photoDisplayUrl: v.displayUrl || null,
              ...to,
            }),
          });
        } catch (_) {
          throw new Error('No connection.');
        }
        let j = null;
        // eslint-disable-next-line no-await-in-loop
        try { j = await r.json(); } catch (_) { j = null; }
        if (!r.ok || !Array.isArray(j)) throw new Error((j && j.error) || `The photo was not posted (${r.status}).`);
        markApiMutated();
        list = j;
        sent++;
        if (words) clear();
        dropPic(which, pic.key);
      }
      return true;
    } catch (e) {
      const why = (e && e.message) || 'The photo was not posted.';
      setReplyErr(sent
        ? `Sent ${sent} of ${photos.length} photos. ${why} The rest are still in the box.`
        : `${why} Nothing was sent.`);
      return false;
    } finally {
      setReplySending(false);
      if (list && defectRef.current === id) {
        stickRef.current = true;
        setComments(list);
        if (sent === photos.length) {
          setSentFlash(flashFor(list));
          setTimeout(() => setSentFlash(''), 4000);
        }
        announcePosted();
        refreshPause();
        loadParties();
      }
    }
  };
  const addFiles = async (files) => {
    if (replySending || !files.length) return;
    const tab = chat;
    const id = defectId;
    setReplyErr('');
    setPreparing((n) => n + 1);
    try {
      const have = (picsRef.current[tab] || []).length;
      const got = await photosForChat(files, MAX_CHAT_PHOTOS - have);
      if (defectRef.current !== id) return;            // the item changed while they were read
      if (got.pics.length) {
        setPicsBy((prev) => ({ ...prev, [tab]: [...(prev[tab] || []), ...got.pics].slice(0, MAX_CHAT_PHOTOS) }));
      }
      const msg = photosForChatError(got);
      if (msg) setReplyErr(msg);
    } finally {
      setPreparing((n) => Math.max(0, n - 1));
    }
  };

  // v0.277.0 — Pause / Resume. Straight to the network, not the offline
  // queue: a pause that waited for reception would not hold anything. Held
  // with nothing paused (a pause removed some other way) resumes too, so a
  // held message can never be stranded.
  const pauseOn = !!(pause && (pause.paused || pause.held > 0));
  const togglePause = async () => {
    if (!pause || pauseBusy) return;
    const resume = pauseOn;
    const heldBefore = pause.held || 0;
    const id = defectId;
    const which = chatKind;
    setPauseBusy(true);
    setPauseErr('');
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new TypeError('offline');
      const r = await fetch('/api/conversation-pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ defectId: id, paused: !resume, ...(showTabs ? { chat: which } : {}) }),
      });
      let j = null;
      try { j = await r.json(); } catch (_) { j = null; }
      if (!r.ok || !j || typeof j.paused !== 'boolean') {
        const err = new Error((j && j.error) || `Nothing changed (${r.status}).`);
        err.server = true;
        throw err;
      }
      if (defectRef.current !== id || kindOf(chatRef.current) !== which) return;
      setPause(j);
      if (resume) {
        // Sent now, stamped with this moment: show the thread as it now is.
        try {
          const list = await apiGet('/api/defect-comments?defectId=' + id, { fresh: true });
          if (defectRef.current === id && Array.isArray(list)) { stickRef.current = true; setComments(list); }
        } catch (_) { /* the live watch re-reads it */ }
        setSentFlash(heldBefore
          ? `Sent ✓ — ${heldBefore === 1 ? 'the held message is' : `the ${heldBefore} held messages are`} on their report and emailed now.`
          : `Sending resumed ✓ — new messages go to ${pauseWho} as you write them.`);
        setTimeout(() => setSentFlash(''), 5000);
        announcePosted({ now: true });
      }
    } catch (e) {
      setPauseErr(e && e.server ? e.message : 'No connection — nothing changed. Try again when you’re back online.');
    } finally {
      setPauseBusy(false);
    }
  };
  // v0.336.0 - never nothing: still reading says so; a read that failed offers
  // Try again; a server without the hold says Pause unavailable.
  const pauseNote = { fontSize: 11, color: theme.textLight, whiteSpace: 'nowrap' };
  const pauseState = !office ? null
    : !pause ? <span data-pause="reading" style={pauseNote}>Checking pause…</span>
    : pause.failed ? (
      <span data-pause="failed" style={{ ...pauseNote, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
        Pause unavailable —
        <button type="button" data-pause-retry onClick={() => { setPause(null); setPauseTry((n) => n + 1); }} style={{
          border: 'none', background: 'none', padding: '4px 0', font: 'inherit', fontWeight: 600,
          color: theme.brand, textDecoration: 'underline', cursor: 'pointer',
        }}>Try again</button>
      </span>
    )
    : !pause.available ? <span data-pause="unavailable" title="Holding messages is not set up on this server." style={pauseNote}>Pause unavailable</span>
    : null;
  const pauseButton = office && pause && pause.available ? (
    <button
      type="button"
      data-pause={pauseOn ? 'on' : 'off'}
      onClick={togglePause}
      disabled={pauseBusy}
      title={pauseOn
        ? `Send the held messages to ${pauseWho} now, and send new ones as you write them.`
        : `Hold every message the office writes to ${pauseWho} on this job until you resume (for a bulk edit).`}
      style={{
        ...btnSecondary,
        fontSize: 11, padding: '3px 8px', flexShrink: 0, whiteSpace: 'nowrap',
        opacity: pauseBusy ? 0.6 : 1,
        cursor: pauseBusy ? 'default' : 'pointer',
      }}
    >{pauseBusy ? (pauseOn ? 'Resuming…' : 'Pausing…')
      : pauseOn ? `Resume sending${pause.held ? ` (${pause.held} held)` : ''}`
      : 'Pause sending'}</button>
  ) : null;

  // v0.279.0 — a marked-up photo from the photo viewer (Brett: "markup and
  // post it to conversation"). Straight to the network, like Add to item
  // photos: three copies of a photo do not belong in the offline queue, and
  // the press should hear a refusal now. The server holds it like any other
  // office message while the conversation is paused.
  const postPhoto = async (photo) => {
    const id = defectId;
    const to = postTo(chat);
    if (showTabs && to.chat === 'trade' && !to.tradeShareId) {
      return { ok: false, error: 'No subcontractor has been sent this item yet. The photo was not posted.' };
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new TypeError('offline');
      const r = await fetch('/api/defect-comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defectId: id,
          photoUrl: photo.photoUrl,
          photoThumbnailUrl: photo.photoThumbnailUrl || null,
          photoDisplayUrl: photo.photoDisplayUrl || null,
          ...to,
        }),
      });
      let j = null;
      try { j = await r.json(); } catch (_) { j = null; }
      if (!r.ok || !Array.isArray(j)) {
        return { ok: false, error: (j && j.error) || `The marked-up photo was not posted (${r.status}).` };
      }
      markApiMutated();
      const mine = j.filter((c) => c.authorRole === 'author' && c.photo).pop();
      const held = !!(mine && mine.heldAt);
      if (defectRef.current === id) {
        stickRef.current = true;
        setComments(j);
        setSentFlash(held ? heldFlash : savedFlash);
        setTimeout(() => setSentFlash(''), 4000);
        announcePosted();
        refreshPause();
      }
      return { ok: true, held };
    } catch (_) {
      return { ok: false, error: 'No connection — the marked-up photo was not posted. Try again when you’re back online.' };
    }
  };

  // v0.283.0 — the item's Save (and its close check) count the words kept in
  // the other tabs too, and Save posts each into its own chat (v0.313.0: each
  // subbie's to that subbie). One refused stops the Save.
  // v0.319.0 — photos waiting count as unsent too: Save sends them, and
  // closing asks first.
  const picsIn = (k) => (picsRef.current[k] || []).length;
  const keptTabs = () => [...new Set([...Object.keys(draftsRef.current), ...Object.keys(picsRef.current)])]
    .filter((k) => k !== chat && ((draftsRef.current[k] || '').trim() || picsIn(k)));
  const draftLine = (words, k) => [words, picsIn(k) ? `(${picsIn(k)} photo${picsIn(k) === 1 ? '' : 's'})` : ''].filter(Boolean).join(' ');
  useImperativeHandle(composerRef, () => ({
    draft: () => [draftLine(replyText.trim(), chat), ...keptTabs().map((k) => draftLine((draftsRef.current[k] || '').trim(), k))].filter(Boolean).join('\n\n'),
    post: async () => {
      if (!(await sendReply())) return false;
      for (const k of keptTabs()) {
        if (!(await sendReply({ body: draftsRef.current[k] || '', chat: k }))) return false;
      }
      return true;
    },
    postPhoto,
  }), [replyText, replySending, defectId, chat, showTabs, parties, picsBy]);

  // v0.272.0 — Brett: "Lets keep the clients photos out of the item photo
  // until i add it." The server copies the reply photo's row onto the item
  // (api/defect-photos, fromCommentId). Straight to the network, not the
  // offline queue: the queue would take it for a photo upload, and this is
  // one deliberate press whose refusal should say so now.
  // Answers true when the photo is on the item now (v0.320.0: Add all stops
  // at the first refusal and says which).
  const addToItem = async (c, { force = false } = {}) => {
    if (adding != null && !force) return false;
    setAdding(c.id);
    setAddErr(null);
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new TypeError('offline');
      const r = await fetch('/api/defect-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defectId, fromCommentId: c.id }),
      });
      let j = null;
      try { j = await r.json(); } catch (_) { j = null; }
      if (!r.ok || !j || j.id == null) {
        const err = new Error((j && j.error) || `The photo was not added (${r.status}).`);
        err.server = true;
        throw err;
      }
      setAddedIds((prev) => new Set(prev).add(c.id));
      if (onAddedToItem) onAddedToItem(j);
      return true;
    } catch (e) {
      setAddErr({
        id: c.id,
        msg: e && e.server ? e.message : 'No connection — the photo was not added. Try again when you’re back online.',
      });
      return false;
    } finally {
      if (!force) setAdding(null);
    }
  };
  const onItem = (c) => !!(c.photo && c.photo.onItem) || addedIds.has(c.id);
  // v0.320.0 — a message of several photos: Add all, one after another.
  const addAllToItem = async (members) => {
    if (adding != null) return;
    try {
      for (const m of members) {
        if (onItem(m)) continue;
        // eslint-disable-next-line no-await-in-loop
        if (!(await addToItem(m, { force: true }))) break;
      }
    } finally {
      setAdding(null);
    }
  };

  // v0.289.0 — remove a message, or the person who wrote it. A removed
  // message stays here, muted, with who removed it; outside the office it
  // reads "Removed by <company>" (their words are never rewritten). Removing
  // a person turns off their links to this report; their messages stay.
  // Straight to the network, as Add to item photos: a refusal says so now.
  const canRemoveMsg = (c) => office && canMod && !c.removedAt && (c.authorRole === 'guest' || c.authorRole === 'author');
  const canRemovePerson = (c) => office && canMod && c.authorRole === 'guest' && c.viaShareId != null && !c.linkRevokedAt;
  const company = brand.name || 'your company';
  // v0.320.0 — `members`: a message of several photos is several messages on
  // the server; Remove message removes every one of them, one after another.
  const moderate = async (c, action, members = [c]) => {
    if (modBusy) return;
    const id = defectId;
    const targets = action === 'remove' ? members.filter((m) => !m.removedAt) : [c];
    if (!targets.length) return;
    let done = 0;
    setModBusy(true);
    setModErr(null);
    try {
      let j = null;
      for (const t of targets) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new TypeError('offline');
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch('/api/comment-moderation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ commentId: t.id, action }),
        });
        j = null;
        // eslint-disable-next-line no-await-in-loop
        try { j = await r.json(); } catch (_) { j = null; }
        if (!r.ok || !j || j.ok !== true) {
          const err = new Error((j && j.error) || `Nothing was removed (${r.status}).`);
          err.server = true;
          throw err;
        }
        done += 1;
        markApiMutated();
      }
      if (defectRef.current !== id) return;
      setModOpen(null);
      setModAsk(null);
      setSentFlash(action === 'remove'
        ? (targets.length > 1
          ? `Removed ✓ — the ${targets.length} photos are gone from their report.`
          : (j.removed === false ? 'That message was already removed.' : `Removed ✓ — their report reads “Removed by ${company}”.`))
        : (j.revoked ? `${j.name ? `${j.name}’s link` : 'Their link'} turned off ✓ — they can no longer open this report or reply.` : 'That link was already off.'));
      setTimeout(() => setSentFlash(''), 5000);
      try {
        const list = await apiGet('/api/defect-comments?defectId=' + id, { fresh: true });
        if (defectRef.current === id && Array.isArray(list)) setComments(list);
      } catch (_) { /* the next live change re-reads it */ }
    } catch (e) {
      const part = done > 0 ? `${done} of ${targets.length} removed — ` : '';
      setModErr({
        id: c.id,
        msg: part + (e && e.server ? e.message : (done > 0
          ? 'the connection dropped. Try again when you’re back online.'
          : 'No connection — nothing was removed. Try again when you’re back online.')),
      });
      if (done > 0) {
        try {
          const list = await apiGet('/api/defect-comments?defectId=' + id, { fresh: true });
          if (defectRef.current === id && Array.isArray(list)) setComments(list);
        } catch (_) { /* the next live change re-reads it */ }
      }
    } finally {
      setModBusy(false);
    }
  };
  // v0.321.0 — flag or clear. `targets`: the messages to change (a message of
  // several photos is several on the server). `errId`: the message a refusal
  // shows under. Straight to the network: a refusal says so now.
  const isFlagged = (c) => !!c.flaggedAt && !c.removedAt;
  const canFlag = (c) => office && !c.removedAt;
  const setFlag = async (targets, action, errId) => {
    if (flagBusy || !targets.length) return;
    const id = defectId;
    let done = 0;
    setFlagBusy(true);
    setModErr(null);
    try {
      for (const t of targets) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new TypeError('offline');
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch('/api/comment-flag', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ commentId: t.id, action }),
        });
        let j = null;
        // eslint-disable-next-line no-await-in-loop
        try { j = await r.json(); } catch (_) { j = null; }
        if (!r.ok || !j || j.ok !== true) {
          const err = new Error((j && j.error) || `Nothing was changed (${r.status}).`);
          err.server = true;
          throw err;
        }
        done += 1;
        markApiMutated();
      }
      if (defectRef.current === id) { setModOpen(null); setModAsk(null); }
    } catch (e) {
      const msg = (done > 0 ? `${done} of ${targets.length} changed — ` : '')
        + (e && e.server ? e.message : 'No connection — nothing was changed. Try again when you’re back online.');
      if (defectRef.current === id) setModErr({ id: errId, msg });
    } finally {
      setFlagBusy(false);
      if (done > 0) {
        announceFlagsChanged(id);
        try {
          const list = await apiGet('/api/defect-comments?defectId=' + id, { fresh: true });
          if (defectRef.current === id && Array.isArray(list)) setComments(list);
        } catch (_) { /* the next live change re-reads it */ }
      }
    }
  };
  const smallBtn = { ...btnSecondary, fontSize: 11, padding: '3px 8px' };
  // v0.322.0 — the tag's hover: who flagged it and when (the banner said this).
  const flagTitle = (members) => {
    const f = members.filter(isFlagged).sort((x, y) => String(y.flaggedAt).localeCompare(String(x.flaggedAt)))[0];
    if (!f) return undefined;
    return `Flagged${f.flaggedByName ? ` by ${f.flaggedByName}` : ''}${f.flaggedViaShareId != null ? ' (through their link)' : ''} · ${fmtDate(f.flaggedAt)}. Clear it from the ⋯.`;
  };
  // The ⋯ at the end of a message's top line: Flag for anyone in the office
  // (v0.321.0), Remove for a moderator. `data-mod-toggle` only where Remove is.
  const modToggle = (c) => {
    const mod = canRemoveMsg(c) || canRemovePerson(c);
    if (!mod && !canFlag(c)) return null;
    const open = modOpen === c.id;
    return (
      <MessageMenuButton
        data-msg-menu={c.id}
        data-mod-toggle={mod ? c.id : undefined}
        open={open}
        title={mod ? 'Flag or remove' : 'Flag'}
        onClick={() => { setModOpen(open ? null : c.id); setModAsk(null); setModErr(null); }}
      />
    );
  };
  // Under the message: the choices, then the one chosen said in full with
  // its confirm and Cancel. Inline, never a browser dialog.
  const modBlock = (c, members = [c]) => {
    const many = members.length > 1;
    const flagged = members.filter(isFlagged);
    const err = modErr && modErr.id === c.id ? modErr.msg : '';
    const errLine = err ? <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.danger }}>{err}</div> : null;
    if (modOpen !== c.id) return errLine;
    const name = c.authorName || 'this person';
    const ask = modAsk;
    return (
      <div data-mod-row={c.id} style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${theme.border}` }}>
        {!ask ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {flagged.length > 0 ? (
              <button type="button" data-flag="clear" disabled={flagBusy}
                onClick={() => setFlag(flagged, 'clear', c.id)}
                style={{ ...smallBtn, opacity: flagBusy ? 0.6 : 1 }}
              >{flagBusy ? 'Clearing…' : 'Clear flag'}</button>
            ) : canFlag(c) && (
              <button type="button" data-flag="flag" disabled={flagBusy}
                onClick={() => setFlag([c], 'flag', c.id)}
                style={{
                  ...smallBtn, color: theme.danger, borderColor: theme.danger, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 4, opacity: flagBusy ? 0.6 : 1,
                }}
              ><Flag size={12} aria-hidden="true" />{flagBusy ? 'Flagging…' : 'Flag: response required'}</button>
            )}
            {canRemoveMsg(c) && (
              <button type="button" data-mod="remove" onClick={() => setModAsk('remove')} style={smallBtn}>Remove message</button>
            )}
            {canRemovePerson(c) && (
              <button type="button" data-mod="remove-person" onClick={() => setModAsk('remove-person')} style={smallBtn}>Remove {name}</button>
            )}
          </div>
        ) : (
          <>
            <div data-mod-ask={ask} style={{ fontSize: 11, color: theme.text, marginBottom: 6 }}>
              {ask === 'remove'
                ? (many
                  ? `All ${members.length} photos go from their report, which reads “Removed by ${company}” in their place. You keep them here, struck.`
                  : `Their report will read “Removed by ${company}” in its place${c.photo ? ', and its photo goes' : ''}. You keep the words here.`)
                : `Turns off ${name}’s links to this report — they can no longer open it or reply. Their messages stay.`}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button
                type="button"
                data-mod-confirm={ask}
                onClick={() => moderate(c, ask, members)}
                disabled={modBusy}
                style={{
                  ...smallBtn, color: theme.danger, borderColor: theme.danger, fontWeight: 600,
                  opacity: modBusy ? 0.6 : 1, cursor: modBusy ? 'default' : 'pointer',
                }}
              >{modBusy ? 'Removing…' : ask === 'remove' ? (many ? `Remove all ${members.length}` : 'Remove message') : 'Turn off their link'}</button>
              <button type="button" onClick={() => { setModAsk(null); setModErr(null); }} disabled={modBusy} style={smallBtn}>Cancel</button>
            </div>
          </>
        )}
        {errLine}
      </div>
    );
  };
  // A removed message: who removed it and when, on its own line.
  const removedLine = (c) => (c.removedAt ? (
    <div data-removed={c.id} style={{ marginTop: 2, marginBottom: 2, fontSize: 11, fontStyle: 'italic', color: theme.textLight }}>
      Removed by {c.removedBy || 'the office'}{c.removedByName ? ` · ${c.removedByName}` : ''} · {fmtDate(c.removedAt)}
    </div>
  ) : null);

  // A comment's photo: the thumbnail and, under a client's one, the office's
  // "Add to item photos" (or that it is on them already).
  const commentPhotoBlock = (c) => {
    if (!(c.photo && (c.photo.thumbnailUrl || c.photo.url))) return null;
    return (
      <>
        <MessagePhoto photo={c.photo} onOpen={() => setCommentPhoto({ list: [c.photo], idx: 0 })} dim={!!c.removedAt} />
        {office && !c.removedAt && (c.authorRole === 'guest' || c.authorRole === 'author') && (
          onItem(c) ? (
            <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.success }}>On item photos ✓</div>
          ) : (
            <button
              type="button"
              onClick={() => addToItem(c)}
              disabled={adding != null}
              style={{
                ...btnSecondary,
                marginTop: 6, fontSize: 11, padding: '3px 8px',
                opacity: adding != null ? 0.6 : 1,
                cursor: adding != null ? 'default' : 'pointer',
              }}
            >{adding === c.id ? 'Adding…' : 'Add to item photos'}</button>
          )
        )}
        {addErr && addErr.id === c.id && (
          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.danger }}>{addErr.msg}</div>
        )}
      </>
    );
  };

  // v0.320.0 — a message of several photos: the squares side by side, each
  // with its own add / on-item mark, then Add all under them.
  const runPhotosBlock = (lead, members) => {
    const canAdd = office && (lead.authorRole === 'guest' || lead.authorRole === 'author');
    const list = members.map((m) => m.photo);
    const allOn = members.every(onItem);
    const err = addErr && members.some((m) => m.id === addErr.id) ? addErr.msg : '';
    const busy = adding != null;
    return (
      <>
        <MessagePhotos
          items={members.map((m, i) => ({
            key: m.id,
            photo: m.photo,
            onOpen: () => setCommentPhoto({ list, idx: i }),
            corner: !canAdd ? null : onItem(m) ? (
              <span
                data-on-item={m.id}
                title="On item photos"
                style={{
                  display: 'block', width: 16, height: 16, borderRadius: 8, lineHeight: '16px',
                  textAlign: 'center', fontSize: 10, fontWeight: 700,
                  color: theme.surface, background: theme.success,
                }}
              >✓</span>
            ) : (
              <button
                type="button"
                data-add-one={m.id}
                title="Add this photo to item photos"
                aria-label="Add this photo to item photos"
                onClick={() => addToItem(m)}
                disabled={busy}
                style={{
                  display: 'block', width: 18, height: 18, padding: 0, borderRadius: 9,
                  border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text,
                  fontSize: 12, fontWeight: 700, lineHeight: '16px', fontFamily: 'inherit',
                  opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer',
                }}
              >{adding === m.id ? '…' : '+'}</button>
            ),
          }))}
        />
        {canAdd && (allOn ? (
          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.success }}>On item photos ✓</div>
        ) : (
          <button
            type="button"
            data-add-all
            onClick={() => addAllToItem(members)}
            disabled={busy}
            style={{
              ...btnSecondary,
              marginTop: 6, fontSize: 11, padding: '3px 8px',
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'default' : 'pointer',
            }}
          >{busy ? 'Adding…' : members.some(onItem) ? 'Add the rest to item photos' : 'Add all to item photos'}</button>
        ))}
        {err && <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.danger }}>{err}</div>}
      </>
    );
  };

  // Same render gate as the original in-sheet section: only an EXISTING
  // defect whose comments fetched successfully shows anything at all. The
  // panel holds a column of its own, so it says what is happening instead.
  if (!defectId) return null;
  if (!Array.isArray(comments)) {
    if (!panel) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}>
        <ConversationLabel live={liveState} onLiveRetry={office ? () => setLiveTry((n) => n + 1) : null} style={{ marginBottom: 5 }}>Conversation</ConversationLabel>
        <div style={{ fontSize: 12, color: loadFailed ? theme.danger : theme.textLight, fontWeight: loadFailed ? 600 : 400 }}>
          {loadFailed ? 'The conversation could not load. Close and reopen the item to try again.' : 'Loading conversation…'}
        </div>
      </div>
    );
  }

  // v0.321.0 — every flagged message on the item, in every chat (tab dots).
  const flaggedAll = office && Array.isArray(comments) ? comments.filter(isFlagged) : [];

  return (
    <>
      {/* v0.123.0 — full-size viewer for a photo attached to a
          conversation entry (recipient question photos, rectify
          evidence). Same shared lightbox as the photo strip. */}
      {/* v0.320.0 - a message of several photos opens on the one pressed and
          steps through the rest. */}
      {commentPhoto && (
        <PhotoLightbox
          contained
          photos={commentPhoto.list.map((p) => ({
            url: p.displayUrl || p.url || p.thumbnailUrl,
            caption: p.caption || null,
          }))}
          initialIdx={commentPhoto.idx}
          onClose={() => setCommentPhoto(null)}
          area={photoArea}
        />
      )}

      {/* v0.122.0 — Conversation. Questions pushed back by the report's
          recipient render framed + brand-accented so they read as
          "someone is waiting on an answer"; every other role renders as
          a plain entry (same treatment as the public report's thread).
          Below it: a reply composer. */}
      <div ref={rootRef} data-part="conversation" style={panel
        ? { display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }
        : { marginTop: 4, marginBottom: 8 }}>
        <ConversationLabel live={liveState} onLiveRetry={office ? () => setLiveTry((n) => n + 1) : null} action={pauseButton || pauseState}
          people={showTabs ? tabPeople(presence) : presence}
          logoUrl={brand.logoUrl} style={{ marginBottom: 5 }}>Conversation</ConversationLabel>
        {/* v0.283.0 — Brett: "Different chat for subbies and clients." One
            tab per chat; a dot marks words kept unsent in another one.
            v0.313.0 — and one tab per subcontractor. */}
        {showTabs && (
          <ChatTabs
            tabs={chatTabs}
            current={chat}
            counts={Object.fromEntries(chatTabs.map(([k]) => [k, comments.filter((c) => inTab(c, k)).length]))}
            kept={Object.fromEntries(chatTabs.map(([k]) => [k, !!((draftsRef.current[k] || '').trim() || (picsBy[k] || []).length)]))}
            flagged={Object.fromEntries(chatTabs.map(([k]) => [k, flaggedAll.some((c) => inTab(c, k))]))}
            onPick={switchChat}
            style={{ marginBottom: 8 }}
          />
        )}
        {office && pause && pause.available && pause.paused && (
          <div data-pause-banner style={{
            marginBottom: 8, padding: '6px 10px', fontSize: 12, color: theme.text,
            background: theme.surface, border: `1px solid ${theme.border}`,
            borderLeft: `3px solid ${theme.warn}`, borderRadius: 6,
          }}>
            <strong>Sending paused</strong>{pause.pausedBy ? ` by ${pause.pausedBy}` : ''} — messages the office writes on this job are saved here but not sent to {pauseWho} until you resume or press Send update.
          </div>
        )}
        {pauseErr && (
          <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 600, color: theme.danger }}>{pauseErr}</div>
        )}
        {shown.length === 0 && (panel || showTabs) && (
          <div data-chat-empty style={{ ...(panel ? { flex: 1, minHeight: 0 } : { marginBottom: 8 }), fontSize: 12, color: theme.textLight }}>
            {!showTabs ? 'No messages on this item yet.'
              : !canWrite ? (Array.isArray(parties)
                ? 'No subcontractor has been sent this item yet. Share it with one and their conversation shows here.'
                : 'Reading who this item has been sent to…')
              : `No messages with ${who} yet.`}
          </div>
        )}
        {shown.length > 0 && (
          <ThreadBox ref={threadRef} onScroll={onThreadScroll} fill={panel} maxHeight={panel ? null : 220} style={{ marginBottom: 8 }}>
            {photoRuns(shown).map(({ lead: c, members }) => {
              // v0.320.0 — photos sent together are one message (photoRuns):
              // a lone message draws exactly as before.
              const many = members.length > 1;
              const seen = many ? runSeenBy(members) : c.seenBy;
              // v0.264.0 — 'guest' = a reply through a share link: the
              // outside person's words, shown like their question.
              // v0.276.0 — each message wears a badge: the writer's initials
              // on the outside person's, the company's logo on the office's.
              // v0.290.0 — drawn by ConversationParts, as the client's page is.
              const outsider = isOutsider(c);
              const flagged = office && members.some(isFlagged);
              return (
                <MessageRow
                  key={c.id}
                  id={c.id}
                  outsider={outsider}
                  removed={!!c.removedAt}
                  flagged={flagged}
                  avatar={<MessageAvatar c={c} brand={brand} />}
                  label={messageLabel(c)}
                  date={c.createdAt}
                  extra={<>{flagged && <FlagTag title={flagTitle(members)} />}{outsider
                    ? (office && c.linkRevokedAt && (
                      <span data-link-off style={{ color: theme.textLight, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>Link turned off</span>
                    ))
                    : (c.heldAt && !c.removedAt && (
                      <span data-held style={{ color: theme.warn, fontWeight: 700, marginLeft: 6 }}>Held — not sent yet</span>
                    ))}</>}
                  action={modToggle(c)}
                >
                  {removedLine(c)}
                  {/* v0.123.0 — a follow-on photo comment carries the
                      placeholder body '(photo)'; the thumbnail says it
                      better, so the placeholder text is hidden. */}
                  {!(c.photo && c.body === '(photo)') && (
                    <MessageText tone={c.removedAt ? 'struck' : 'plain'}>{c.body}</MessageText>
                  )}
                  {many ? runPhotosBlock(c, members) : commentPhotoBlock(c)}
                  {modBlock(c, members)}
                  {/* v0.280.0 - Brett: "Yes to the seen marks". Who on the
                      item's links has had this message on screen (each person
                      once, under the newest office message they have seen). */}
                  {!outsider && Array.isArray(seen) && seen.length > 0 && (
                    <div
                      data-seen-by
                      title={seen.map(p => `${p.name} - ${seenWhen(p.at)}`).join('\n')}
                      style={{ marginTop: 3, fontSize: 11, color: theme.textLight }}
                    >
                      Seen by {seen.map(p => p.name).join(', ')}{seen[0].at ? ` · ${seenWhen(seen[0].at)}` : ''}
                    </div>
                  )}
                </MessageRow>
              );
            })}
          </ThreadBox>
        )}
        {canWrite && <Composer
          value={replyText}
          onChange={(v) => { setReplyText(v); if (beaconRef.current) beaconRef.current.typing(); }}
          onSend={() => sendReply()}
          sending={replySending}
          rows={panel ? 3 : 2}
          placeholder={showTabs
            ? (pause && pause.paused ? `Write to ${who} — held until you resume…` : `Write to ${who}…`)
            : pause && pause.paused ? 'Write a reply — held until you resume…' : comments.length ? 'Write a reply…' : 'Add a comment…'}
          error={replyErr}
          flash={sentFlash}
          flashTone={sentFlash.startsWith('Held') ? 'warn' : 'success'}
          {...(office ? {
            canSend: !replySending && !preparing && !!(replyText.trim() || pics.length),
            attachments: <PhotoAttachments pics={pics} busy={replySending} onRemove={(key) => dropPic(chat, key)} />,
            tools: <AddPhotosButton onFiles={addFiles} max={MAX_CHAT_PHOTOS} full={replySending || pics.length >= MAX_CHAT_PHOTOS} note={preparing ? 'Preparing photos…' : pics.length ? `${pics.length} of ${MAX_CHAT_PHOTOS}` : ''} />,
            onFiles: addFiles,
          } : {})}
        />}
      </div>
    </>
  );
}
